import { Fragment, useEffect, useState } from 'react';
import type { TrafficSummaryResponse, VisitEvent, VisitorGroup } from '@bpd/shared';
import { trafficApi } from '../../api/traffic.ts';

type LoadState = 'loading' | 'ready' | 'error' | 'not-configured';

const ctDayFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Chicago',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Calendar-day key (YYYY-MM-DD) in Central time — `byDay` keys are bucketed the same way. */
function ctDayKey(date: Date): string {
  return ctDayFormatter.format(date);
}

function sumLastNDays(byDay: Record<string, number>, n: number): number {
  const [y, m, d] = ctDayKey(new Date()).split('-').map(Number) as [number, number, number];
  const cutoffKey = new Date(Date.UTC(y, m - 1, d - n)).toISOString().slice(0, 10);
  return Object.entries(byDay).reduce(
    (total, [day, count]) => (day >= cutoffKey ? total + count : total),
    0,
  );
}

function formatCT(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    timeZone: 'America/Chicago',
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

/** Visitor's own local time, if their timezone was resolved — just the clock time, not the date. */
function formatLocal(iso: string, timezone: string | undefined): string {
  if (!timezone) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      timeZone: timezone,
      timeStyle: 'short',
    });
  } catch {
    return '—';
  }
}

/** Bare hostname from a referrer URL (e.g. "bing.com"), or "Direct" if there wasn't one. */
function referrerDomain(referrer: string | undefined): string {
  if (!referrer) return 'Direct';
  try {
    return new URL(referrer).hostname.replace(/^www\./, '');
  } catch {
    return referrer;
  }
}

function StatTile({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-gray-200 p-5 shadow-sm">
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className="mt-1 font-serif text-3xl font-bold text-phoenix-blue-900">{value}</p>
    </div>
  );
}

/** Horizontal bar list — single sequential hue, direct value labels, native-title hover. */
function BarList({ title, data }: { title: string; data: [string, number][] }) {
  const max = Math.max(1, ...data.map(([, count]) => count));
  return (
    <div className="rounded-lg border border-gray-200 p-5 shadow-sm">
      <h2 className="text-sm font-semibold text-gray-700">{title}</h2>
      {data.length === 0 ? (
        <p className="mt-3 text-sm text-gray-400">No data in this window.</p>
      ) : (
        <ul className="mt-4 space-y-2">
          {data.map(([label, count]) => (
            <li key={label} className="flex items-center gap-3" title={`${label}: ${count}`}>
              <span className="w-28 flex-shrink-0 truncate text-sm text-gray-600">{label}</span>
              <div className="h-2 flex-1 rounded-full bg-gray-100">
                <div
                  className="h-2 rounded-full bg-phoenix-blue-500"
                  style={{ width: `${(count / max) * 100}%` }}
                />
              </div>
              <span className="w-10 flex-shrink-0 text-right text-sm font-medium text-gray-700">
                {count}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function VisitorBadge({ isNew }: { isNew: boolean }) {
  return (
    <span
      className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${
        isNew ? 'bg-phoenix-ember-500/15 text-phoenix-ember-600' : 'bg-phoenix-blue-100 text-phoenix-blue-700'
      }`}
    >
      {isNew ? 'New' : 'Returning'}
    </span>
  );
}

/** Shown only when true — a hosting/backbone ASN match, not a certainty, so an absence says nothing either way. */
function BotBadge() {
  return (
    <span
      className="inline-block rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700"
      title="The network operator (ASN) for this IP is a known cloud/hosting/backbone provider, not a residential or mobile ISP — likely a bot, scanner, or proxy rather than a person browsing."
    >
      Likely bot
    </span>
  );
}

/** Nested per-visit event table, shown inside an expanded visitor row. */
function VisitEventTable({ ipPrefix, country, region, timezone, events }: {
  ipPrefix: string;
  country?: string | undefined;
  region?: string | undefined;
  timezone?: string | undefined;
  events: VisitEvent[];
}) {
  if (events.length === 0) return <p className="py-2 text-xs text-gray-400">No visits in this window.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-100 text-xs">
        <thead>
          <tr className="text-left text-gray-500 uppercase tracking-wide">
            <th className="px-3 py-1.5 font-semibold">Time (CT)</th>
            <th className="px-3 py-1.5 font-semibold">Local Time</th>
            <th className="px-3 py-1.5 font-semibold">IP Prefix</th>
            <th className="px-3 py-1.5 font-semibold">Country</th>
            <th className="px-3 py-1.5 font-semibold">State</th>
            <th className="px-3 py-1.5 font-semibold">Referrer</th>
            <th className="px-3 py-1.5 font-semibold">OS</th>
            <th className="px-3 py-1.5 font-semibold">Browser</th>
            <th className="px-3 py-1.5 font-semibold">Device</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {events.map((ev) => (
            <tr key={ev.timestamp}>
              <td className="px-3 py-1.5 whitespace-nowrap text-gray-700">{formatCT(ev.timestamp)}</td>
              <td className="px-3 py-1.5 whitespace-nowrap text-gray-600">
                {formatLocal(ev.timestamp, timezone)}
              </td>
              <td className="px-3 py-1.5 font-mono text-gray-600">{ipPrefix}</td>
              <td className="px-3 py-1.5 text-gray-600">{country ?? '—'}</td>
              <td className="px-3 py-1.5 text-gray-600">{region ?? '—'}</td>
              <td className="px-3 py-1.5 text-gray-600" title={ev.referrer ?? undefined}>
                {referrerDomain(ev.referrer)}
              </td>
              <td className="px-3 py-1.5 text-gray-600">{ev.os}</td>
              <td className="px-3 py-1.5 text-gray-600">{ev.browser}</td>
              <td className="px-3 py-1.5 text-gray-600">{ev.deviceType}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Visitor table — one row per IP prefix, click/caret expands to individual visits. */
function VisitorTable({ visitors }: { visitors: VisitorGroup[] }) {
  const [expanded, setExpanded] = useState<string | null>(null);

  if (visitors.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-700">Visitors</h2>
        <p className="mt-3 text-sm text-gray-400">No visitors in this window.</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-100 px-5 py-3">
        <h2 className="text-sm font-semibold text-gray-700">Visitors</h2>
        <p className="text-xs text-gray-400">
          Grouped by IP prefix — the closest thing to an identity for anonymous visitors.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                IP Prefix
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                Location
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                Network
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                First Seen (CT)
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                Last Seen (CT)
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                Visits (all-time)
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                Status
              </th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visitors.map((v) => {
              const isExp = expanded === v.ipPrefix;
              return (
                <Fragment key={v.ipPrefix}>
                  <tr
                    key={v.ipPrefix}
                    className="cursor-pointer transition-colors hover:bg-gray-50"
                    onClick={() => setExpanded(isExp ? null : v.ipPrefix)}
                  >
                    <td className="px-4 py-2.5 font-mono text-gray-700 whitespace-nowrap">{v.ipPrefix}</td>
                    <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">
                      {[v.city, v.region, v.country].filter(Boolean).join(', ') || '—'}
                    </td>
                    <td
                      className="px-4 py-2.5 text-gray-600 whitespace-nowrap"
                      title={v.hostname ?? undefined}
                    >
                      {v.org ?? '—'}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-600 whitespace-nowrap">
                      {formatCT(v.firstSeenAt)}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-600 whitespace-nowrap">
                      {formatCT(v.lastSeenAt)}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">
                      {v.allTimeVisitCount}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <VisitorBadge isNew={v.isNew} />
                        {v.isLikelyBot && <BotBadge />}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right text-xs text-phoenix-blue-500">
                      {isExp ? '▲' : '▼'}
                    </td>
                  </tr>
                  {isExp && (
                    <tr key={`${v.ipPrefix}-events`}>
                      <td colSpan={8} className="bg-gray-50 px-4 py-3">
                        <VisitEventTable
                          ipPrefix={v.ipPrefix}
                          country={v.country}
                          region={v.region}
                          timezone={v.timezone}
                          events={v.events}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function TrafficPage() {
  const [state, setState] = useState<LoadState>('loading');
  const [summary, setSummary] = useState<TrafficSummaryResponse | null>(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    trafficApi
      .getSummary(days)
      .then((data) => {
        if (cancelled) return;
        if (!data) {
          setState('not-configured');
          return;
        }
        setSummary(data);
        setState('ready');
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [days]);

  return (
    <main className="mx-auto max-w-5xl px-6 py-16">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-3xl font-bold text-phoenix-blue-900">Site Traffic</h1>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>
      <p className="mt-2 text-sm text-gray-500">
        Public-site visits only (bots excluded). Owners&apos; own logged-in usage isn&apos;t
        counted. &quot;New&quot; vs &quot;Returning&quot; is based on the last 180 days of
        history (older visits age out automatically).
      </p>

      {state === 'not-configured' && (
        <p className="mt-8 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          The traffic backend isn&apos;t connected yet in this environment.
        </p>
      )}
      {state === 'error' && (
        <p className="mt-8 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          Couldn&apos;t load traffic data. Try again in a moment.
        </p>
      )}
      {state === 'loading' && <p className="mt-8 text-sm text-gray-500">Loading…</p>}

      {state === 'ready' && summary && (
        <div className="mt-8 space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <StatTile label={`Total visits (${days}d)`} value={summary.totalVisits} />
            <StatTile label="Today" value={sumLastNDays(summary.byDay, 0)} />
            <StatTile label="Last 7 days" value={sumLastNDays(summary.byDay, 7)} />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <StatTile
              label={`Get in touch submissions (${days}d)`}
              value={summary.totalSubmissions}
            />
            <StatTile label="Today" value={sumLastNDays(summary.submissionsByDay, 0)} />
            <StatTile label="Last 7 days" value={sumLastNDays(summary.submissionsByDay, 7)} />
          </div>

          <BarList
            title="Visits by day"
            data={Object.entries(summary.byDay).sort((a, b) => (a[0] < b[0] ? 1 : -1))}
          />

          <VisitorTable visitors={summary.visitors} />
        </div>
      )}
    </main>
  );
}
